import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaRobot, FaSyncAlt, FaProjectDiagram, FaUser, FaFlag, FaChevronRight, FaDatabase, FaCircleNotch } from 'react-icons/fa';
import { fmtMs, fmtCost, fmtDateTime, compactJson, statusClass, runTitle } from './aiTrace/format';
import './AiRunPanel.css';

// The « Runs IA » tab of a ticket (docs/ai-trace-spec.md §6.2, §13 step 14):
// a compact, chronological list of the ticket's runs. Opening a run — or
// the whole chronology — happens in the full-screen timeline overlay
// (AiTimelineOverlay), never inline: one place to explore, left → right.
//
// It also carries the operator's « re-run » form (spec §6.3): corrected
// identifiers (tracking number, order reference, client, carrier named by
// the sender) are handed to the ticket, the tenant's data sources run again
// on them and the AI processing restarts on the refreshed facts.

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

const EMPTY_FORM = { tracking_number: '', order_number: '', client_name: '', carrier_name: '' };

// What the form starts with: the identifiers the ticket carries today. The
// carrier field is a hint (the carrier the sender names), never prefilled
// from the ticket: a stale carrier would contradict a corrected order.
const prefillFrom = (ticket) => {
    const ctx = ticket && ticket.advanced_context && typeof ticket.advanced_context === 'object' ? ticket.advanced_context : {};
    const client = ticket && ticket.metadata && ticket.metadata.client && typeof ticket.metadata.client === 'object' ? ticket.metadata.client : {};
    return {
        tracking_number: (ticket && ticket.tracking_number) || ctx.tracking_number || '',
        // The order number the form or the agent recorded (num_commande),
        // else the one an integration put in the context (KI-193).
        order_number: ctx.num_commande || ctx.order_number || '',
        client_name: ctx.shop_name || client.name || '',
        carrier_name: '',
    };
};

const AiRunPanel = ({ ticketId, ticket, onOpenRun, onOpenTimeline, onRerunDone }) => {
    const { t, i18n } = useTranslation();
    const [runs, setRuns] = useState(null);
    const [total, setTotal] = useState(0);
    const [listError, setListError] = useState(null);
    const [rerunOpen, setRerunOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [restartAi, setRestartAi] = useState(true);
    const [rerunBusy, setRerunBusy] = useState(false);
    const [rerunResult, setRerunResult] = useState(null);
    const [rerunError, setRerunError] = useState(null);

    const loadList = useCallback(async () => {
        if (!ticketId) return;
        setListError(null);
        try {
            const response = await axios.get(`/api/tickets/${ticketId}/ai-runs?limit=100`, authHeaders());
            setRuns(response.data.runs || []);
            setTotal(response.data.total || 0);
        } catch (e) {
            setRuns([]);
            setListError(t('aiRunPanel.loadError', 'Impossible de charger les runs IA.'));
        }
    }, [ticketId, t]);

    useEffect(() => { loadList(); }, [loadList]);

    const openRerun = () => {
        setForm(prefillFrom(ticket));
        setRerunResult(null);
        setRerunError(null);
        setRerunOpen(true);
    };

    const canSubmitRerun = Boolean((form.tracking_number || '').trim() || (form.order_number || '').trim());

    const submitRerun = async (event) => {
        event.preventDefault();
        if (rerunBusy || !canSubmitRerun) return;
        setRerunBusy(true);
        setRerunError(null);
        setRerunResult(null);
        try {
            const payload = { restart_ai: restartAi };
            Object.entries(form).forEach(([key, value]) => {
                const trimmed = (value || '').trim();
                if (trimmed) payload[key] = trimmed;
            });
            const response = await axios.post(`/api/tickets/${ticketId}/enrichment/rerun`, payload, authHeaders());
            setRerunResult(response.data);
            loadList();
            // The AI restart runs in the background: refresh the list once it
            // has had time to open its run.
            setTimeout(loadList, 5000);
            if (onRerunDone) onRerunDone(response.data);
        } catch (e) {
            const detail = e.response && e.response.data && e.response.data.detail;
            setRerunError(typeof detail === 'string' ? detail : t('aiRunPanel.rerun.error', 'La relance a échoué.'));
        } finally {
            setRerunBusy(false);
        }
    };

    const renderRerunResult = () => {
        if (!rerunResult) return null;
        const enrichment = rerunResult.enrichment || {};
        const ids = enrichment.identifiers || {};
        const parts = [];
        if (enrichment.found) {
            parts.push(t('aiRunPanel.rerun.found', { defaultValue: 'Données trouvées via {{source}}', source: enrichment.source || '?' }));
        } else {
            parts.push(t('aiRunPanel.rerun.notFound', 'Aucune donnée trouvée pour ces identifiants'));
        }
        if (enrichment.carrier_name) parts.push(`${t('aiRunPanel.rerun.carrierLabel', 'transporteur')} ${enrichment.carrier_name}`);
        if (ids.tracking_number) parts.push(`${t('aiRunPanel.rerun.trackingLabel', 'suivi')} ${ids.tracking_number}`);
        if (ids.client_name) parts.push(`${t('aiRunPanel.rerun.clientLabel', 'client')} ${ids.client_name}`);
        if (enrichment.is_shipped === '1' || enrichment.is_shipped === '0') {
            parts.push(`${t('aiRunPanel.rerun.shipped', 'expédié')} ${enrichment.is_shipped === '1' ? t('aiRunPanel.rerun.yes', 'oui') : t('aiRunPanel.rerun.no', 'non')}`);
        }
        if (enrichment.is_delivered === '1') parts.push(t('aiRunPanel.rerun.delivered', 'livré'));
        (enrichment.conflicts || []).forEach((conflict) => {
            parts.push(t('aiRunPanel.rerun.conflict', {
                defaultValue: 'conflit transporteur : l’expéditeur indique {{client}}, la commande part avec {{found}}',
                client: conflict.client_carrier || '?',
                found: conflict.effitrace_carrier || '?',
            }));
        });
        const restart = rerunResult.restart;
        return (
            <div className={`arp-rerun-result ${enrichment.found ? 'ok' : 'warn'}`}>
                <div>{parts.join(' · ')}</div>
                {restart && (
                    <div className="arp-muted">
                        {t('aiRunPanel.rerun.restarted', {
                            defaultValue: 'Traitement IA relancé ({{count}} brouillon(s) écarté(s)) — le nouveau run apparaît ci-dessous dans quelques secondes.',
                            count: restart.deleted_ai_messages || 0,
                        })}
                    </div>
                )}
            </div>
        );
    };

    const renderRerunForm = () => {
        if (!rerunOpen) return null;
        const fields = [
            ['tracking_number', t('aiRunPanel.rerun.tracking', 'Numéro de suivi')],
            ['order_number', t('aiRunPanel.rerun.order', 'Référence de commande')],
            ['client_name', t('aiRunPanel.rerun.client', 'Client (tel que configuré)')],
            ['carrier_name', t('aiRunPanel.rerun.carrier', 'Transporteur indiqué par l’expéditeur (facultatif)')],
        ];
        return (
            <form className="arp-rerun" onSubmit={submitRerun}>
                <p className="arp-rerun-intro">
                    {t('aiRunPanel.rerun.intro', 'Transmettez au ticket des identifiants corrigés : les sources de données du compte (EffiTrace, connecteurs…) sont réinterrogées et, par défaut, le traitement IA est relancé sur les données à jour. Un champ vide garde la valeur actuelle du ticket.')}
                </p>
                <div className="arp-rerun-grid">
                    {fields.map(([key, label]) => (
                        <label key={key} className="arp-rerun-field">
                            <span>{label}</span>
                            <input
                                type="text"
                                className="arp-rerun-input"
                                value={form[key]}
                                onChange={(ev) => setForm({ ...form, [key]: ev.target.value })}
                                disabled={rerunBusy}
                            />
                        </label>
                    ))}
                </div>
                <label className="arp-rerun-check">
                    <input type="checkbox" checked={restartAi} onChange={(ev) => setRestartAi(ev.target.checked)} disabled={rerunBusy} />
                    <span>{t('aiRunPanel.rerun.restart', 'Relancer le traitement IA sur les données à jour')}</span>
                </label>
                <div className="arp-rerun-actions">
                    <button type="button" className="arp-rerun-cancel" onClick={() => setRerunOpen(false)} disabled={rerunBusy}>
                        {t('aiRunPanel.rerun.cancel', 'Fermer')}
                    </button>
                    <button type="submit" className="arp-rerun-submit" disabled={rerunBusy || !canSubmitRerun}>
                        {rerunBusy
                            ? <><FaCircleNotch className="arp-spin" /> {t('aiRunPanel.rerun.running', 'Relance en cours…')}</>
                            : <><FaDatabase /> {t('aiRunPanel.rerun.submit', 'Relancer')}</>}
                    </button>
                </div>
                {rerunError && <div className="arp-error">{rerunError}</div>}
                {renderRerunResult()}
            </form>
        );
    };

    const renderCounters = (summary) => {
        if (!summary) return null;
        const items = [
            ['llm_calls', t('aiRunPanel.counters.llm', 'appels modèle')],
            ['tool_calls', t('aiRunPanel.counters.tools', 'outils')],
            ['decisions', t('aiRunPanel.counters.decisions', 'décisions')],
            ['effects', t('aiRunPanel.counters.effects', 'effets')],
            ['externals', t('aiRunPanel.counters.externals', 'appels externes')],
            ['automations', t('aiRunPanel.counters.automations', 'automatisations')],
            ['feedback', t('aiRunPanel.counters.feedback', 'retours')],
            ['errors', t('aiRunPanel.counters.errors', 'erreurs')],
        ].filter(([key]) => typeof summary[key] === 'number' && summary[key] > 0);
        const cost = fmtCost(summary.total_cost_usd);
        return (
            <div className="arp-counters">
                {items.map(([key, label]) => (
                    <span key={key} className={`arp-chip ${key === 'errors' ? 'arp-chip-error' : ''}`}>{summary[key]} {label}</span>
                ))}
                {cost && <span className="arp-chip">{cost}</span>}
            </div>
        );
    };

    const renderRun = (run, nested = false) => {
        const actor = run.trigger?.actor?.type;
        const Icon = actor === 'employee' || actor === 'admin' ? FaUser : FaFlag;
        return (
            <div key={run._id} className={`arp-run ${nested ? 'nested' : ''}`}>
                <button type="button" className="arp-run-header" onClick={() => onOpenRun && onOpenRun(run._id)} title={run._id}>
                    <span className={`arp-service arp-service-${run.service}`}>{run.service}</span>
                    <span className="arp-run-title">
                        <Icon /> {runTitle(run, t)}
                        {actor && actor !== 'system' && <span className="arp-muted"> · {actor}</span>}
                    </span>
                    <span className="arp-run-right">
                        <span className="arp-muted">{fmtDateTime(run.started_at, i18n.language)}</span>
                        <span className="arp-duration">{fmtMs(run.duration_ms)}</span>
                        <span className={`arp-pill arp-pill-${statusClass(run.status)}`}>{t(`aiRunPanel.status.${run.status}`, run.status)}</span>
                        <FaChevronRight className="arp-run-toggle" />
                    </span>
                </button>
                {renderCounters(run.summary)}
                {run.error && <div className="arp-error-inline">{run.error.type}: {compactJson(run.error.message, 140)}</div>}
                {run.children && run.children.length > 0 && (
                    <div className="arp-children">{run.children.map((child) => renderRun(child, true))}</div>
                )}
            </div>
        );
    };

    return (
        <div className="ai-run-panel">
            <div className="arp-header">
                <h3><FaRobot /> {t('aiRunPanel.title', 'Runs IA')} {runs && <span className="arp-muted">({total})</span>}</h3>
                <div className="arp-header-actions">
                    <button
                        type="button"
                        className={`arp-rerun-toggle ${rerunOpen ? 'active' : ''}`}
                        onClick={rerunOpen ? () => setRerunOpen(false) : openRerun}
                        title={t('aiRunPanel.rerun.toggle', 'Relancer avec de nouvelles valeurs')}
                    >
                        <FaDatabase /> {t('aiRunPanel.rerun.toggle', 'Relancer avec de nouvelles valeurs')}
                    </button>
                    <button type="button" className="arp-open-timeline" onClick={() => onOpenTimeline && onOpenTimeline()}>
                        <FaProjectDiagram /> {t('aiTimeline.open', 'Ouvrir la chronologie')}
                    </button>
                    <button type="button" className="arp-refresh" onClick={loadList} title={t('aiRunPanel.refresh', 'Actualiser')}><FaSyncAlt /></button>
                </div>
            </div>
            {renderRerunForm()}
            <p className="arp-intro">
                {t('aiRunPanel.intro', 'Chaque run est un traitement IA sur ce ticket : ses appels au modèle, ses outils, ses décisions, ses effets et les retours humains, dans l’ordre.')}
            </p>
            {listError && <div className="arp-error">{listError}</div>}
            {runs === null && !listError && <div className="arp-muted">{t('aiRunPanel.loading', 'Chargement…')}</div>}
            {runs && runs.length === 0 && !listError && (
                <div className="arp-empty">{t('aiRunPanel.empty', 'Aucun run IA enregistré pour ce ticket.')}</div>
            )}
            {runs && runs.map((run) => renderRun(run))}
        </div>
    );
};

export default AiRunPanel;
